import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { EXC_SEVERITY, EXCEPTION_STATUS, fmtDate, fmtMoney } from "../../lib/labels";
import { userName, useUsers } from "../../lib/hooks";
import { Chip, EmptyState, PageHeader } from "../../components/common";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const EXC_ACTIONS = {
  detectada: ["asignar", "solicitar_informacion", "resolver", "aceptar_excepcionalmente", "escalar", "rechazar"],
  asignada: ["solicitar_informacion", "resolver", "aceptar_excepcionalmente", "escalar", "rechazar"],
  pendiente_informacion: ["resolver", "aceptar_excepcionalmente", "escalar"],
  pendiente_correccion: ["resolver"],
  escalada: ["asignar", "resolver", "aceptar_excepcionalmente"],
};
const ACTION_LABELS = {
  asignar: "Asignar", solicitar_informacion: "Solicitar información", resolver: "Resolver",
  aceptar_excepcionalmente: "Aceptar excepcionalmente", escalar: "Escalar", rechazar: "Rechazar",
};
const ACTION_FIELDS = {
  asignar: ["owner_id"], solicitar_informacion: ["motivo"], resolver: ["resolucion", "evidence"],
  aceptar_excepcionalmente: ["justificacion"], escalar: ["motivo"], rechazar: ["motivo"],
};
const FIELD_LABELS = { owner_id: "Responsable", motivo: "Motivo", resolucion: "Resolución aplicada", evidence: "Evidencia / referencia", justificacion: "Justificación de la aceptación extraordinaria" };

function ExceptionDialog({ exc, action, open, onClose, onDone }) {
  const { users } = useUsers();
  const [fields, setFields] = useState({});
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/finanzas/exceptions/${exc.id}/action`, { action, ...fields });
      toast.success(`Excepción ${ACTION_LABELS[action].toLowerCase()} — registrada en auditoría`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const req = ACTION_FIELDS[action] || [];
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent data-testid="exception-action-dialog">
        <DialogHeader><DialogTitle>{ACTION_LABELS[action]} — {exc?.type}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          {action === "aceptar_excepcionalmente" && (
            <p className="rounded-sm border border-red-200 bg-red-50 p-2 text-xs text-red-800">
              La aceptación extraordinaria queda registrada con tu identidad y se notifica a Dirección. Reservada a casos justificados.
            </p>
          )}
          {req.map((f) => f === "owner_id" ? (
            <div key={f}>
              <Label>{FIELD_LABELS[f]} *</Label>
              <Select value={fields.owner_id || ""} onValueChange={(v) => setFields({ ...fields, owner_id: v })}>
                <SelectTrigger data-testid="exc-field-owner"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          ) : (
            <div key={f}>
              <Label>{FIELD_LABELS[f]}{f !== "evidence" ? " *" : ""}</Label>
              <Textarea value={fields[f] || ""} onChange={(e) => setFields({ ...fields, [f]: e.target.value })} data-testid={`exc-field-${f}`} />
            </div>
          ))}
          <Button className="w-full" onClick={submit}
            disabled={busy || req.filter((f) => f !== "evidence").some((f) => !String(fields[f] || "").trim())}
            data-testid="exc-action-confirm">{busy ? "Registrando…" : "Confirmar"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Excepciones() {
  const { map } = useUsers();
  const [status, setStatus] = useState("");
  const [severity, setSeverity] = useState("");
  const [dialog, setDialog] = useState(null); // {exc, action}
  const { data: items, refetch } = useQuery({
    queryKey: ["fin-exceptions", status, severity],
    queryFn: async () => (await api.get("/finanzas/exceptions", { params: { status: status || undefined, severity: severity || undefined } })).data,
  });

  return (
    <div data-testid="excepciones-page">
      <PageHeader title="Excepciones" subtitle="Desviaciones detectadas por los controles: asignación, resolución, escalado o aceptación extraordinaria justificada." />
      <div className="mb-4 flex flex-wrap gap-2">
        <Select value={status || "all"} onValueChange={(v) => setStatus(v === "all" ? "" : v)}>
          <SelectTrigger className="w-60" data-testid="exc-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            {Object.entries(EXCEPTION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={severity || "all"} onValueChange={(v) => setSeverity(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44" data-testid="exc-severity-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toda severidad</SelectItem>
            {Object.entries(EXC_SEVERITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {items && items.length === 0 && <EmptyState title="Sin excepciones" description="Las excepciones se generan automáticamente desde los controles de matching, presupuesto y pagos." />}
      {items?.length > 0 && (
        <div className="space-y-3">
          {items.map((e) => (
            <div key={e.id} className="ts-card p-4" data-testid={`exception-${e.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-slate-400">{e.type}</span>
                    <Chip map={EXC_SEVERITY} value={e.severity} testid={`exc-severity-${e.id}`} />
                    <Chip map={EXCEPTION_STATUS} value={e.status} testid={`exc-status-${e.id}`} />
                    {e.extraordinary && <span className="ts-chip ts-chip-info">extraordinaria</span>}
                  </div>
                  <p className="mt-1.5 text-sm text-slate-800">{e.description}</p>
                  <p className="mt-1 text-xs text-slate-500">
                    {e.entity_type} {e.entity_id} · importe {e.affected_amount ? fmtMoney(e.affected_amount) : "—"} · responsable: <strong>{userName(map, e.owner_id)}</strong> · límite {fmtDate(e.due_date)}
                  </p>
                  {e.proposed_resolution && <p className="mt-1 text-xs text-slate-500">Resolución propuesta: {e.proposed_resolution}</p>}
                  {e.resolution_note && <p className="mt-1 text-xs text-emerald-700">Resolución: {e.resolution_note} ({userName(map, e.resolved_by)})</p>}
                  {e.entity_type === "invoice" && <Link to={`/finanzas/facturas/${e.entity_id}`} className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline">Abrir factura →</Link>}
                  {e.entity_type === "supplier" && <Link to={`/finanzas/proveedores?supplier=${e.entity_id}`} className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline">Abrir proveedor →</Link>}
                  {e.entity_type === "purchase_request" && <Link to={`/finanzas/solicitudes/${e.entity_id}`} className="mt-1 inline-block text-xs font-medium text-blue-700 hover:underline">Abrir solicitud →</Link>}
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {(EXC_ACTIONS[e.status] || []).map((a) => (
                    <Button key={a} size="sm" variant={a === "resolver" ? "default" : a === "aceptar_excepcionalmente" ? "destructive" : "outline"}
                      onClick={() => { setDialog({ exc: e, action: a }); }} data-testid={`exc-${a}-${e.id}`}>
                      {ACTION_LABELS[a]}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      {dialog && <ExceptionDialog exc={dialog.exc} action={dialog.action} open onClose={() => setDialog(null)} onDone={() => { setDialog(null); refetch(); }} />}
    </div>
  );
}
